import React, { ChangeEvent, useEffect, useState } from 'react'
import { Autocomplete, Box, Chip, FormControl, InputLabel, MenuItem, Select, TextField, Theme } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { makeStyles } from '@mui/styles'

export interface DropDownElement {
  key: string
  value: string
  color?: string
}

interface IDropDownComponentData {
  options: DropDownElement[]
  disableAutocomplete?: boolean
  multiple?: boolean
  id?: string
  name?: string
  margin?: 'normal' | 'dense' | 'none'
  label: string
  value?: string
  multipleValue?: string[]
  inputValue?: string
  required?: boolean
  defaultValue?: any
  onChange?: (value: DropDownElement | undefined) => void
  onChangeList?: (value: DropDownElement[]) => void
  onInputChange?: (newInputValue: string) => void
  disableClearable?: boolean
  disabled?: boolean
  placeholder?: string
  limitTags?: number | undefined
  size?: 'medium' | 'small' | undefined
  helperText?: string
  error?: boolean
  variant?: 'outlined' | 'filled' | 'standard'
  fullWidth?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
  },
}))
const DropDownComponent: React.FC<IDropDownComponentData> = (props) => {
  const classes = useStyles()

  const extractValue = (): DropDownElement | null => {
    let response: DropDownElement | null = null
    if (typeof (props.value) === 'string') {
      const elem = props.value && props.options.find((elem: DropDownElement) => elem.key === props.value)
      if (elem) {
        response = elem
      }
    } else {
      response = null
    }
    return response
  }

  const extractMultipleValue = (): DropDownElement[] | null => {
    const array: DropDownElement[] = []
    if (props.multipleValue) {
      props.multipleValue.map(elem => {
        const element = props.options.find(option => option.key === elem)
        if (element) {
          array.push(element)
        }
      })
    }
    return array
  }

  const [value, setValue] = useState<DropDownElement | null>(extractValue())
  const [multipleValue, setMultipleValue] = useState<DropDownElement[] | null>(extractMultipleValue())

  useEffect(() => {
    !props.multiple && setValue(extractValue())
    props.multiple && setMultipleValue(extractMultipleValue())
  }, [props.value, props.multipleValue],
  )

  return (
    <>
      {props.disableAutocomplete ? (
        <FormControl className={classes.root}>
          <InputLabel>{props.label}</InputLabel>
          <Select
            id={props.id}
            value={props.multiple ? props.multipleValue : value?.key as string}
            label={props.label}
            onChange={(event) => {
              const element = props.options.find(elem => elem.key === event.target.value as string)
              props.onChange && props.onChange(element)
            }}
            fullWidth={props.fullWidth}
            displayEmpty
            renderValue={(selected) => {
              if (Array.isArray(selected)) {
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                      />
                    ))}
                  </Box>
                )
              }
              return ''
            }}
          >
            {props.options.map((elem) => (
              <MenuItem key={elem.key} value={elem.key}>{elem.value}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Autocomplete
          options={props.options}
          multiple={props.multiple ? props.multiple : false}
          value={props.multiple ? multipleValue : value}
          inputValue={props.inputValue}
          onInputChange={(event, newInputValue) => props.onInputChange && props.onInputChange(newInputValue)}
          id={props.id}
          size={props.size}
          limitTags={props.limitTags}
          defaultValue={multipleValue}
          isOptionEqualToValue={(option: any, value) => option.value === value.value}
          getOptionLabel={(option: any) => option.value}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={index}
                style={{ backgroundColor: (option.color && props.multiple) ? option.color : '' }}
                label={option.value}
              />
            ))}
          renderOption={(prop, option, { selected }) => (
            <li
              {...prop}
              style={{ borderRight: (option.color && !props.multiple) ? `3px solid ${option.color}` : '' }}
            >
              <div dangerouslySetInnerHTML={{ __html: option.value }} />
            </li>
          )}
          onChange={(event: ChangeEvent<any>, value: any) => {
            if (props.multiple) {
              setMultipleValue(value)
              props.onChangeList && props.onChangeList(value)
            } else {
              setValue(value)
              props.onChange && props.onChange(value)
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={props.label}
              name={props.name}
              placeholder={props.placeholder}
              required={props.required}
              helperText={props.helperText}
              error={props.error}
              InputLabelProps={{
                shrink: true,
              }}
              variant={props.variant}
              margin={props.margin}
            />
          )}
          className={classes.root}
          disableClearable={props.disableClearable ? props.disableClearable : false}
          disabled={props.disabled ? props.disabled : false}
          popupIcon={<KeyboardArrowDownIcon />}
          fullWidth={props.fullWidth}
        />
      )}
    </>
  )
}
export default DropDownComponent
